import React, { useState, useEffect, useRef } from 'react';
import './AccountDatePicker.css';

/**
 * AccountDatePicker - Specialized DatePicker for Account Information screen
 * Year-agnostic birthday selector - only day and month are relevant
 * Year is stored as a fixed value (2000) but not shown to user
 */
const AccountDatePicker = ({ 
  value, 
  onChange, 
  name, 
  id, 
  placeholder = 'Select your birthday', 
  className = '', 
  required = false,
  format = 'DD/MM', // Birthday format without year
  label,
  disabled = false,
}) => {
  // State for the calendar visibility
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  // State for year picker dropdown
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);
  
  // State for current month being viewed
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // State for the selected date (from the value prop)
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Fixed year for all birthdays (year is not relevant for birthday celebrations)
  const FIXED_YEAR = 2000;
  
  
  // Ref for the calendar popup to handle outside clicks
  const calendarRef = useRef(null);
  
  // Ref for year picker dropdown
  const yearPickerRef = useRef(null);
  
  // Ref for the input container to position the calendar
  const inputContainerRef = useRef(null);
  
  // No complex positioning - just simple dropdown
  
  // Effect to parse the initial value if any
  useEffect(() => {
    if (value) {
      try {
        // Attempt to parse the date from the value
        const parsedDate = parseInputDate(value);
        if (parsedDate && !isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
        }
      } catch (error) {
        console.error("Error parsing date:", error);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);
  
  // Effect to handle clicking outside the calendar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
        setIsYearPickerOpen(false);
      }
    };
    
    // Add event listener when calendar is open
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Remove event listener when component unmounts or calendar closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);
  
  
  // Helper to parse the input date string
  const parseInputDate = (dateString) => {
    if (!dateString) return null;
    
    try {
      // Handle different date formats
      let dateParts;
      
      // ISO format (YYYY-MM-DD)
      if (dateString.includes('-')) {
        dateParts = dateString.split('-');
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2]);
        
        // Validate the parsed values
        if (year < 1900 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
          return null;
        }
        
        const date = new Date(year, month, day);
        // Check if the date is valid (e.g., not 31st of February)
        if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
          return null;
        }
        
        return date;
      }
      
      // DD/MM/YYYY format
      if (dateString.includes('/')) {
        dateParts = dateString.split('/');
        if (dateParts.length === 3) {
          const day = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const year = parseInt(dateParts[2]);
          
          // Validate the parsed values
          if (year < 1900 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
            return null;
          }
          
          const date = new Date(year, month, day);
          // Check if the date is valid (e.g., not 31st of February)
          if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
            return null;
          }
          
          return date;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };
  
  // Helper to format the date for display based on format prop
  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    // Format based on format prop
    if (format === 'DD/MM/YYYY') {
      return `${day}/${month}/${year}`;
    } else {
      // Default DD/MM format (for birthdays)
      return `${day}/${month}`;
    }
  };
  
  // Handler for date selection - uses format prop to determine if year matters
  const handleDateSelect = (day, month = currentMonth.getMonth()) => {
    // For DD/MM/YYYY format (like Joined HMS), use actual calendar year
    // For DD/MM format (like Birthday), use fixed year
    const yearToUse = format === 'DD/MM/YYYY' ? currentMonth.getFullYear() : FIXED_YEAR;
    const newDate = new Date(yearToUse, month, day);
    setSelectedDate(newDate);
    
    console.log('Selected date:', {
      day,
      month: month + 1,
      year: yearToUse,
      format,
      dateObject: newDate
    });
    
    // Update the current month being viewed to match the selected date
    setCurrentMonth(new Date(yearToUse, month, 1));
    
    // Format the date according to the format prop
    const formattedDate = formatDate(newDate);
    console.log('Formatted date being sent:', formattedDate);
    
    if (onChange) {
      const event = {
        target: {
          name,
          value: formattedDate
        }
      };
      onChange(event);
    }
    
    // Close the calendar after selection
    setIsCalendarOpen(false);
  };
  
  
  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  // Year navigation removed - we only care about day and month for birthdays
  
  // Toggle calendar visibility
  const toggleCalendar = () => {
    if (!disabled) {
      setIsCalendarOpen(!isCalendarOpen);
      
      // If opening the calendar and we have a selected date, set the current month to that date
      if (!isCalendarOpen && selectedDate) {
        setCurrentMonth(new Date(FIXED_YEAR, selectedDate.getMonth(), 1));
      } else if (!isCalendarOpen && !selectedDate) {
        // If no date selected, start at January (using fixed year)
        setCurrentMonth(new Date(FIXED_YEAR, 0, 1));
      }
    }
  };
  
  // Handler for manual input in the text field
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // If the input value is empty, clear the selectedDate and pass null to parent
    if (!inputValue || inputValue.trim() === '') {
      setSelectedDate(null);
      
      if (onChange) {
        onChange({
          target: {
            name,
            value: null // Send null instead of empty string to prevent backend validation errors
          }
        });
      }
      return;
    }
    
    // For non-empty values, try to parse the date
    const inputDate = parseInputDate(inputValue);
    if (inputDate && !isNaN(inputDate.getTime())) {
      setSelectedDate(inputDate);
      
      // Format the date according to the specified format
      const formattedDate = formatDate(inputDate);
      
      if (onChange) {
        onChange({
          target: {
            name,
            value: formattedDate
          }
        });
      }
    } else {
      // If date is invalid, still call onChange but with the raw value
      if (onChange) {
        onChange(e);
      }
    }
  };
  
  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Initialize the calendar array with empty cells for the days before the first day of the month
    const calendarDays = Array(firstDayOfWeek).fill(null);
    
    // Add days of the month
    for (let day = 1; day <= totalDays; day++) {
      calendarDays.push(day);
    }
    
    return calendarDays;
  };
  
  // Month names for header
  const monthNames = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  // Day names for header
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  
  // Get the name of the current month and year
  const currentMonthName = monthNames[currentMonth.getMonth()];
  const currentYear = currentMonth.getFullYear();
  
  // Render the calendar days
  const calendarDays = generateCalendarDays();
  
  // Check if a day is the selected day
  const isSelectedDay = (day) => {
    if (!selectedDate || !day) return false;
    
    return (
      selectedDate.getDate() === day && 
      selectedDate.getMonth() === currentMonth.getMonth() && 
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };
  
  // Check if a day is today
  const isToday = (day) => {
    if (!day) return false;
    
    const today = new Date();
    return (
      today.getDate() === day && 
      today.getMonth() === currentMonth.getMonth() && 
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  return (
    <div className={`account-date-picker-container ${className} ${isCalendarOpen ? 'calendar-open' : ''}`}>
      {label && (
        <label htmlFor={id || name} className="account-date-picker-label">
          {label} {required && <span className="required-field">*</span>}
        </label>
      )}
      
      <div className="account-date-picker-input-container" ref={inputContainerRef}>
        <input
          type="text"
          id={id || name}
          name={name}
          value={value || ''}
          onChange={handleInputChange}
          placeholder={placeholder}
          className={`account-date-picker-input ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          required={required}
          readOnly={true}
          onClick={toggleCalendar}
          aria-label="Date of birth input"
        />
        
        <button 
          type="button" 
          onClick={toggleCalendar} 
          className={`account-date-picker-button ${disabled ? 'disabled' : ''}`}
          disabled={disabled}
          aria-label="Open birth date calendar"
        >
          <CalendarIcon />
        </button>
        
      </div>
      
      {isCalendarOpen && (
        <div 
          className="account-date-picker-calendar"
          ref={calendarRef}
        >
          <div className="account-calendar-header">
            <button 
              type="button" 
              className="account-calendar-nav-button" 
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              <span className="account-calendar-nav-icon">&#10094;</span>
            </button>
            
            <div className="account-calendar-header-title">
              <span className="account-calendar-month-name">{currentMonthName}</span>
              <div className="account-calendar-year-picker-container">
                <button 
                  type="button"
                  className="account-calendar-year-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsYearPickerOpen(!isYearPickerOpen);
                  }}
                  aria-label="Select year"
                >
                  {currentYear}
                </button>
                {isYearPickerOpen && (
                  <div 
                    className="account-calendar-year-dropdown"
                    ref={yearPickerRef}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="year-dropdown-scroll">
                      {Array.from({ length: 100 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <button
                            key={year}
                            type="button"
                            className={`year-option ${year === currentYear ? 'selected' : ''}`}
                            onClick={() => {
                              setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
                              setIsYearPickerOpen(false);
                            }}
                          >
                            {year}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <button 
              type="button" 
              className="account-calendar-nav-button" 
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              <span className="account-calendar-nav-icon">&#10095;</span>
            </button>
          </div>
          
          <div className="account-calendar-days-header">
            {dayNames.map((day, index) => (
              <div key={index} className="account-calendar-day-name">{day}</div>
            ))}
          </div>
          
          <div className="account-calendar-days-grid">
            {calendarDays.map((day, index) => (
              <div 
                key={index} 
                className={`account-calendar-day-cell ${!day ? 'empty-cell' : ''} ${isSelectedDay(day) ? 'selected' : ''} ${isToday(day) ? 'today' : ''}`}
                onClick={() => day && handleDateSelect(day)}
              >
                {day}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar icon component
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 8C3 6.34315 4.34315 5 6 5H18C19.6569 5 21 6.34315 21 8V18C21 19.6569 19.6569 21 18 21H6C4.34315 21 3 19.6569 3 18V8Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 13H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 13H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 13H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 17H8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M16 17H16.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default AccountDatePicker;
